import { useState } from 'react'

// Cosmos-style auth chrome, shared by Login / Signup / ForgotPassword: a dark
// split screen — wordmark top-left, a faint oversized brand mark centered on
// the left panel — and a centered right column with the mark, the title,
// an "or …" subline, pill fields with inset labels, and a white pill button.
// Always dark regardless of app theme (the `auth-dark` class also scopes an
// autofill override in styles.css so Chrome can't paint the pills white).
//
// TYPE IS THE APP'S OWN. The reference set its title in a serif, and this
// shell copied that — the only serif anywhere in the product, on the first
// screen anyone sees (user, 2026-10-13: "completely off the branding"). The
// title now uses the heading style every signed-in page uses (ui.jsx
// PageHeader: bold, -0.02em tracking, the system sans stack TAILWIND_STYLE.md
// prescribes), sized up for a standalone screen. Nothing here may add a
// webfont or a `font-serif`.

export default function AuthShell({ title, subline, children, below }) {
  return (
    <div className="auth-dark flex min-h-screen bg-[#0f0f0f] text-[#f5f5f5]">
      {/* Left — brand panel */}
      <div className="relative hidden flex-1 items-center justify-center [border-right:1px_solid_#1d1d1d] lg:flex">
        <div className="absolute left-7 top-6 flex items-center gap-x-2.5">
          <img src="/logo-on-dark.png?v=5" alt="" className="h-[18px] w-[18px]" />
          <span className="text-[15px] font-semibold tracking-[0.01em]">Revailo Sales</span>
        </div>
        <img src="/logo-on-dark.png?v=5" alt="" className="h-[230px] w-[230px] opacity-[0.06]" />
      </div>

      {/* Right — form column */}
      <div className="flex min-h-screen flex-1 items-center justify-center px-6 py-12">
        <div className="w-full max-w-[400px]">
          <div className="flex flex-col items-center">
            <img src="/logo-on-dark.png?v=5" alt="" className="h-[34px] w-[34px]" />
            <h1 className="m-0 mt-6 text-center text-[28px] font-bold tracking-[-0.02em] text-[#f5f5f5]">
              {title}
            </h1>
            {subline && (
              <div className="mt-2 text-center text-[13.5px] text-[#8f8f8f] [&_a]:text-[#8f8f8f] [&_a]:underline [&_a]:underline-offset-[3px] [&_a:hover]:text-[#cfcfcf]">
                {subline}
              </div>
            )}
          </div>
          <div className="mt-10 flex flex-col gap-y-3">{children}</div>
          {below && <div className="mt-7 text-center">{below}</div>}
        </div>
      </div>
    </div>
  )
}

// Pill field with the label inset above the value, Cosmos-style.
export function AuthField({ label, trailing, inputRef, ...inputProps }) {
  return (
    <label className="block cursor-text rounded-[26px] bg-[#1b1b1b] px-6 py-[13px] transition-colors [border:1px_solid_#262626] focus-within:[border-color:#3f3f3f]">
      <span className="block text-[12px] leading-[1.3] text-[#8f8f8f]">{label}</span>
      <span className="mt-[2px] flex items-center gap-x-3">
        <input
          ref={inputRef}
          className="w-full min-w-0 border-none bg-transparent p-0 text-[15px] text-[#f5f5f5] outline-none placeholder:text-[#565656]"
          {...inputProps}
        />
        {trailing}
      </span>
    </label>
  )
}

// White pill submit — the "Enter" button.
export function AuthButton({ children, ...props }) {
  return (
    <button
      type="submit"
      className="mt-2 w-full rounded-full bg-white py-[15px] text-[15px] font-medium text-[#101010] transition-colors hover:bg-[#e9e9e9] disabled:cursor-not-allowed disabled:opacity-60"
      {...props}
    >
      {children}
    </button>
  )
}

export function AuthError({ children }) {
  if (!children) return null
  return <div className="text-center text-[13px] leading-[1.5] text-[#f87171]">{children}</div>
}

// Password field with a show/hide eye, matching the reference's trailing icon.
export function AuthPasswordField({ label, ...inputProps }) {
  const [show, setShow] = useState(false)
  return (
    <AuthField
      label={label}
      type={show ? 'text' : 'password'}
      trailing={
        <button
          type="button"
          tabIndex={-1}
          aria-label={show ? 'Hide password' : 'Show password'}
          onClick={() => setShow((v) => !v)}
          className="flex-none border-none bg-transparent p-0 text-[#6b6b6b] transition-colors hover:text-[#cfcfcf]"
        >
          {show ? (
            <svg viewBox="0 0 24 24" fill="none" className="h-[18px] w-[18px]" aria-hidden="true">
              {/* coolicons: line/Show.svg */}
              <path d="M3.58631 13.7788C5.36575 15.5478 8.46904 17.9999 11.9997 17.9999C15.5303 17.9999 18.6331 15.5478 20.4125 13.7788C20.8818 13.3123 21.1172 13.0782 21.2667 12.6201C21.3733 12.2933 21.3733 11.7067 21.2667 11.3799C21.1172 10.9218 20.8818 10.6877 20.4125 10.2211C18.633 8.45208 15.5303 6 11.9997 6C8.46904 6 5.36575 8.45208 3.58631 10.2211C3.11665 10.688 2.8818 10.9216 2.73231 11.3799C2.62569 11.7067 2.62569 12.2933 2.73231 12.6201C2.8818 13.0784 3.11665 13.3119 3.58631 13.7788Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
              <path d="M9.99951 12C9.99951 13.1046 10.8949 14 11.9995 14C13.1041 14 13.9995 13.1046 13.9995 12C13.9995 10.8954 13.1041 10 11.9995 10C10.8949 10 9.99951 10.8954 9.99951 12Z" stroke="currentColor" strokeWidth="1.8" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" fill="none" className="h-[18px] w-[18px]" aria-hidden="true">
              {/* coolicons: line/Hide.svg */}
              <path d="M3.9994 4L19.9994 20M16.4994 16.7559C15.1468 17.4845 13.618 17.9999 11.9994 17.9999C8.46875 17.9999 5.36575 15.5478 3.58632 13.7788C3.11661 13.3119 2.8818 13.0784 2.73231 12.6201C2.6257 12.2933 2.62567 11.7066 2.73231 11.3797C2.88185 10.9215 3.11715 10.6875 3.58778 10.2197C4.48467 9.32821 5.71752 8.26359 7.17171 7.42676M19.4994 14.6335C19.8325 14.3405 20.1375 14.0523 20.4112 13.7803L20.4141 13.7772C20.8827 13.3114 21.1177 13.0779 21.2669 12.6206C21.3736 12.2938 21.3733 11.7068 21.2667 11.38C21.1173 10.9219 20.8822 10.6877 20.4128 10.2211C18.6334 8.45208 15.5301 6 11.9994 6C11.6619 6 11.3283 6.02241 10.9994 6.06448M13.3223 13.5C12.9697 13.8112 12.5066 14 11.9994 14C10.8948 14 9.9994 13.1046 9.9994 12C9.9994 11.4605 10.213 10.9711 10.5603 10.6113" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
        </button>
      }
      {...inputProps}
    />
  )
}
